/**
 * Task Service
 * Handles task management operations
 */
import { apiGet, apiPost, apiPut, apiDelete, apiCall } from './apiUtils';

const taskService = {
  /**
   * Get all tasks
   * @param {Object} [filters={}] - Optional filters for tasks
   * @returns {Promise<Object>} Tasks data
   */
 getTasks: async (filters = {}) => {
  try {
    console.log('Fetching tasks with filters:', filters);

    // 🔥 Convert filters to query string
    const queryString = new URLSearchParams(filters).toString();

    const response = await apiGet(`/api/tasks?${queryString}`);

    console.log('Final URL:', `/api/tasks?${queryString}`);
    console.log('Tasks response:', response);

    return response;
  } catch (error) {
    console.error('Error fetching tasks:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch tasks',
      error: error
    };
  }
},


  /**
   * Get task by ID
   * @param {string} id - Task ID
   * @returns {Promise<Object>} Task details
   */
  getTaskById: async (id) => {
    try {
      console.log(`Fetching task details for ID: ${id}`);
      const response = await apiGet(`/api/tasks/${id}`);

      console.log('Task details response:', response);

      return response;
    } catch (error) {
      console.error(`Error fetching task ${id}:`, error);
      return {
        success: false,
        message: error.message || 'Failed to fetch task details',
        error: error
      };
    }
  },

  /**
   * Create new task
   * @param {Object} taskData - Task details
   * @returns {Promise<Object>} Created task response
   */
  createTask: async (taskData) => {
    try {
      console.log('Creating new task:', taskData);
      const response = await apiPost('/api/tasks', taskData);

      console.log('Task creation response:', response);

      return response;
    } catch (error) {
      console.error('Error creating task:', error);
      return {
        success: false,
        message: error.message || 'Failed to create task',
        error: error
      };
    }
  },

  /**
   * Update task
   * @param {string} id - Task ID
   * @param {Object} taskData - Updated task details
   * @returns {Promise<Object>} Update response
   */
  updateTask: async (id, taskData) => {
    try {
      console.log(`Updating task ${id}:`, taskData);
      const response = await apiPut(`/api/tasks/${id}`, taskData);

      console.log('Task update response:', response);

      return response;
    } catch (error) {
      console.error(`Error updating task ${id}:`, error);
      return {
        success: false,
        message: error.message || 'Failed to update task',
        error: error
      };
    }
  },

  /**
   * Delete task
   * @param {string} id - Task ID
   * @returns {Promise<Object>} Delete response
   */
  deleteTask: async (id) => {
    try {
      console.log(`Deleting task ${id}`);
      const response = await apiDelete(`/api/tasks/${id}`);

      console.log('Task deletion response:', response);

      return response;
    } catch (error) {
      console.error(`Error deleting task ${id}:`, error);
      return {
        success: false,
        message: error.message || 'Failed to delete task',
        error: error
      };
    }
  },

  /**
   * Update task status
   * @param {string} id - Task ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Status update response
   */
  updateTaskStatus: async (id, status) => {
    try {
      console.log(`Updating task ${id} status to ${status}`);
      const response = await apiCall(`/api/tasks/${id}/status`, {
        method: 'PATCH',
        body: { status }
      });

      console.log('Task status update response:', response);

      return response;
    } catch (error) {
      console.error(`Error updating task ${id} status:`, error);
      return {
        success: false,
        message: error.message || 'Failed to update task status',
        error: error
      };
    }
  },

  /**
   * Assign task to user
   * @param {string} taskId - Task ID
   * @param {string} userId - User ID to assign task to
   * @returns {Promise<Object>} Task assignment response
   */
  assignTask: async (taskId, userId) => {
    try {
      console.log(`Assigning task ${taskId} to user ${userId}`);
      const response = await apiPost(`/api/tasks/${taskId}/assign`, { userId });

      console.log('Task assignment response:', response);

      return response;
    } catch (error) {
      console.error(`Error assigning task ${taskId} to user ${userId}:`, error);
      return {
        success: false,
        message: error.message || 'Failed to assign task',
        error: error
      };
    }
  },

  /**
   * Add comment to task
   * @param {string} taskId - Task ID
   * @param {string} comment - Comment text
   * @returns {Promise<Object>} Comment addition response
   */
  addComment: async (taskId, comment) => {
    try {
      console.log(`Adding comment to task ${taskId}`);
      const response = await apiPost(`/api/tasks/${taskId}/comments`, { comment });

      console.log('Add comment response:', response);

      return response;
    } catch (error) {
      console.error(`Error adding comment to task ${taskId}:`, error);
      return {
        success: false,
        message: error.message || 'Failed to add comment',
        error: error
      };
    }
  },

  /**
   * Get task comments
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>} Task comments
   */
  getComments: async (taskId) => {
    try {
      console.log(`Fetching comments for task ${taskId}`);
      const response = await apiGet(`/api/tasks/${taskId}/comments`);

      console.log('Task comments response:', response);

      return response;
    } catch (error) {
      console.error(`Error fetching comments for task ${taskId}:`, error);
      return {
        success: false,
        message: error.message || 'Failed to fetch task comments',
        error: error
      };
    }
  },

  /**
   * Get task categories
   * @returns {Promise<Object>} Task categories
   */
  getCategories: async () => {
    try {
      console.log('Fetching task categories');
      const response = await apiGet('/api/tasks/categories');

      console.log('Task categories response:', response);

      return response;
    } catch (error) {
      console.error('Error fetching task categories:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch task categories',
        error: error
      };
    }
  },

  /**
   * Create task category
   * @param {Object} categoryData - Category details
   * @returns {Promise<Object>} Category creation response
   */
  createCategory: async (categoryData) => {
    try {
      console.log('Creating task category:', categoryData);
      const response = await apiPost('/api/tasks/categories', categoryData);

      console.log('Task category creation response:', response);

      return response;
    } catch (error) {
      console.error('Error creating task category:', error);
      return {
        success: false,
        message: error.message || 'Failed to create task category',
        error: error
      };
    }
  },

  /**
   * Add feedback to task
   * @param {string} taskId - Task ID
   * @param {string} feedback - Feedback text
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} Feedback addition response
   */
  addFeedback: async (taskId, feedback, metadata = {}) => {
    try {
      console.log(`Adding feedback to task ${taskId}:`, { feedback, metadata });
      const response = await apiPost(`/api/tasks/${taskId}/feedback`, {
        feedback,
        ...metadata
      });

      console.log('Task feedback response:', response);

      return response;
    } catch (error) {
      console.error(`Error adding feedback to task ${taskId}:`, error);
      return {
        success: false,
        message: error.message || 'Failed to add task feedback',
        error: error
      };
    }
  }
};

export default taskService;